import { SeedingStrategy } from "../shared/interfaces";
import { IWorklistToolInternalState } from "../shared/state/initial-state";
import { checkForDuplicateBarcodes } from "../shared/WorklistHelpers";
import {
  buildSplitVolDictionary,
  getVolRemaining,
  intToDestWellMappingsPresent,
} from "./ExternalThawToolHelpers";
import {
  DestPlateInfoState,
  IntermediatePlateInfoState,
  SourcePlateInfoState,
  WorklistValuesState,
} from "./state";
import {
  IExternalThawInternalState,
  IExternalThawMethodSettings,
} from "./state/initial-state";

export const getExtThawRequiredWorklistWarnings = (
  sourcePlateInfo: SourcePlateInfoState[],
  destPlateInfo: DestPlateInfoState[],
  intPlateInfo: IntermediatePlateInfoState[],
  deadPlateType: string,
  worklistValues: WorklistValuesState,
  internalState: IExternalThawInternalState,
  worklistToolState: IWorklistToolInternalState
) => {
  let warning: string[] = [];
  const methodSettings = internalState.methodSettings;
  const splitVolDict = buildSplitVolDictionary(worklistValues.int1ToDest);

  const generalErrors = Object.values(worklistToolState.errorMessages).filter(
    (message) => message !== ""
  );
  const requiredMessages = checkForRequiredMethodSettings(methodSettings);
  const vSpingRangeErrors = checkVSpinRange(methodSettings);

  warning = [...generalErrors, ...requiredMessages, ...vSpingRangeErrors];

  const MIN_NUMBER_OF_ALIQUOTS = 1;
  const MAX_NUMBER_OF_ALIQUOTS = 3;
  if (
    methodSettings.numberOfAliquots < MIN_NUMBER_OF_ALIQUOTS ||
    methodSettings.numberOfAliquots > MAX_NUMBER_OF_ALIQUOTS
  ) {
    warning.push("Number of Aliquots must be within 1-3");
  }

  if (!internalState.sourcePlateHasTubeBarcodeScan) {
    warning.push(
      "At least one source plate doesn't have a barcode scan. Please upload a barcode scan associated with each source plate."
    );
  }

  if (![5, 10, 15, 20, 25].includes(methodSettings.aliquotVolume)) {
    warning.push("Valid Aliquot Volume is required");
  }

  if (methodSettings.incubationTime < 5 || methodSettings.incubationTime > 60) {
    warning.push("Incubation time must be between 5 and 60 mins");
  }

  if (methodSettings.experimentSettings === "") {
    warning.push(
      "Experiement Settings empty. Set Dead Total Aliquots and/or add wells to Int Plate"
    );
  }

  if (
    intPlateInfo.some((e) => e.resuspensionVol <= 0 || isNaN(e.resuspensionVol))
  ) {
    warning.push("Pellet Resuspension Vol Resuspension Vol is Required");
  }

  if (
    intPlateInfo.some(
      (e) =>
        !isNaN(e.resuspensionVol) &&
        (e.resuspensionVol < 200 || e.resuspensionVol > 2000)
    )
  ) {
    warning.push("Pellet Resuspension Vol must be between 200 and 2000");
  }

  if (
    methodSettings.numberOfAliquots * getTotalNumSourceWells(sourcePlateInfo) >
    96
  ) {
    warning.push(
      "You have more aliquots than there are wells in the dead total plate. Reduce number of aliquots."
    );
  }

  if (
    worklistValues.int1ToDest.some(
      (wellMapping) =>
        wellMapping.transferVol === "" || wellMapping.transferVol === undefined
    )
  ) {
    warning.push(
      `${
        methodSettings.seedingStrategy === SeedingStrategy.CellCount
          ? "Cellcount"
          : "TransferVol"
      } cannot be empty.`
    );
  }

  if (
    methodSettings.seedingStrategy === SeedingStrategy.CellCount &&
    worklistValues.int1ToDest.some(
      (wellMapping) =>
        wellMapping.transferVol &&
        (parseInt(wellMapping.transferVol) < 1000 ||
          parseInt(wellMapping.transferVol) > 20000000)
    )
  ) {
    warning.push("Cellcount must be between 1000 and 20 Million");
  }

  //volumetric + splitevenly
  if (
    methodSettings.seedingStrategy === SeedingStrategy.Volumetric &&
    methodSettings.splitSeedingVolEvenly &&
    worklistValues.int1ToDest.some(
      (wellMapping) =>
        wellMapping.transferVol &&
        parseInt(wellMapping.transferVol) >
          destPlateInfo[wellMapping.destPlateIndex].topUpVol
    )
  ) {
    warning.push(
      "Transfer Volume cannot be greater than Final Target Well Volume. Increase Number of splits in dest plate or Final Target Well Volume OR reduce Resuspension Vol."
    );
  }

  //volumetric + custom vol
  if (
    methodSettings.seedingStrategy === SeedingStrategy.Volumetric &&
    !methodSettings.splitSeedingVolEvenly &&
    worklistValues.int1ToDest.some(
      (wellMapping) =>
        wellMapping.transferVol &&
        parseInt(wellMapping.transferVol) >
          destPlateInfo[wellMapping.destPlateIndex].topUpVol
    )
  ) {
    warning.push(
      "Transfer Volume cannot be greater than Final Target Well Volume. Increase Final Target Well Volume OR adjust transfer volume in Dest Well Mapping"
    );
  }

  if (
    !!intPlateInfo[0].intWellVol &&
    !!intPlateInfo[0].operatingVol &&
    intPlateInfo[0].intWellVol > intPlateInfo[0].operatingVol
  ) {
    warning.push(
      "Int Well Volume cannot be greater than Int Plate operating volume."
    );
  }

  if (
    destPlateInfo.some(
      (destPlate) => destPlate.topUpVol > destPlate.operatingVol
    )
  ) {
    warning.push(
      "Target Final Well Volume (Topup) exceeds operating volume on at least one Dest Plate."
    );
  }

  if (
    destPlateInfo.some(
      (destPlate) => destPlate.topUpVol < destPlate.minOperatingVol
    )
  ) {
    warning.push(
      "Target Final Well Volume (Topup) is less than minimum operating volume on at least one Dest Plate."
    );
  }

  if (
    methodSettings.seedingStrategy === SeedingStrategy.Volumetric &&
    worklistValues.int1ToDest.some((wellMapping) => {
      const volRemaining = getVolRemaining(
        wellMapping.sourceWellId,
        splitVolDict,
        intPlateInfo[0].resuspensionVol,
        methodSettings.numberOfAliquots,
        methodSettings.aliquotVolume
      );
      return volRemaining < 0;
    })
  ) {
    warning.push(
      "You're attempting to transfer more volume than is available. Increase Resuspension Vol or reduce Transfer Vol."
    );
  }

  if (!intToDestWellMappingsPresent(worklistValues)) {
    warning.push("Missing Intermediate to Destination Plate Mapping");
  }

  if (checkForDuplicateBarcodes(sourcePlateInfo)) {
    warning.push("Duplicate Source Plate Barcodes exist");
  }

  if (checkForDuplicateBarcodes(destPlateInfo)) {
    warning.push("Duplicate Destination Plate Barcode exist");
  }

  if (deadPlateType === "") {
    warning.push("Dead Plate Type must be selected");
  }

  if (intPlateInfo[0].labwareTypeCode === "") {
    warning.push("Int Plate Type must be selected");
  }
  return warning;
};

const checkVSpinRange = (methodSettings: IExternalThawMethodSettings) => {
  const vSpinSettingRange: {
    key: keyof IExternalThawMethodSettings;
    displayValue: string;
    min: number;
    max: number;
  }[] = [
    {
      key: "spinParamPercent",
      displayValue: "Spin Param(%)",
      min: 5,
      max: 100,
    },
    { key: "spinTime", displayValue: "Spin Time", min: 1, max: 10 },
    {
      key: "spinAccel",
      displayValue: "Acceleration Settings",
      min: 5,
      max: 100,
    },
    {
      key: "spinDecel",
      displayValue: "Deceleration Settings",
      min: 5,
      max: 100,
    },
  ];

  const requiredMessage = [];
  for (const setting of vSpinSettingRange) {
    const settingValue = methodSettings[setting.key] as number;
    if (settingValue < setting.min || settingValue > setting.max) {
      requiredMessage.push(
        `${setting.displayValue} must be between ${setting.min} and ${setting.max}`
      );
    }
  }

  return requiredMessage;
};

const checkForRequiredMethodSettings = (
  methodSettings: IExternalThawMethodSettings
) => {
  const methodSettingDisplayValues: Map<
    keyof IExternalThawMethodSettings,
    string
  > = new Map([["selectedSystem", "Selected System"]]);

  const requiredFieldMissing = (key: keyof IExternalThawMethodSettings) =>
    methodSettings[key] === undefined ||
    ["", "-1", "0"].includes(methodSettings[key]!.toString());

  const requiredMessages = [...methodSettingDisplayValues.keys()]
    .filter((key) => requiredFieldMissing(key))
    .map((key) => `${methodSettingDisplayValues.get(key)} is a required field`);

  return [...requiredMessages];
};

const getTotalNumSourceWells = (sourcePlateInfo: SourcePlateInfoState[]) => {
  let totalWells = 0;
  for (const sourcePlate of sourcePlateInfo) {
    totalWells += sourcePlate.wellInfo.length;
  }
  return totalWells;
};
